/**
 * The outcome of importing one file in Stage 1 (5A / C02).
 *
 * Until now the Sorting Floor knew two end states, DONE and ERROR, and the outer import chose between them without looking at what the
 * extraction actually produced: every PDF was "Processed" although its record said extractionFailed, every video was "Transcribed" because
 * its SIZE was acceptable, and a reload painted every artefact DONE whatever had happened. This module is the one contract all three
 * (the live completion, the saved record, the reload reconstruction) read, so a file is described the same way at every point:
 *
 *   extracted       text was read in full, or the file is a photo
 *   partial         some of the content was read (contentComplete false or contentTruncated true), and the file is kept
 *   transcribed     a video was analysed and its transcript published
 *   reference_only  a video kept as evidence without transcription (above the processing cap, or skipped)
 *   failed          nothing usable was produced; the file itself is still kept
 *   cancelled       the operator stopped the import before this file finished
 *
 * Status is derived: failed and cancelled are ERROR rows (counted as unresolved by the Sorting Floor and by the reconciliation guard);
 * every other outcome is a DONE row, with a visible caption where the outcome is not the plain success.
 *
 * Outcomes are read only from facts the import itself recorded (an extractionFailed flag, content flags, a video row's processed / error /
 * cancelled / processingMode fields), never inferred from a file's size or from a stale prepared row. A record that carries none of those
 * facts (older projects) reads as a plain success, exactly as it always did.
 */
import { recordedVideoTranscript } from './video-transcript-notes';

export type ImportOutcome = 'extracted' | 'partial' | 'transcribed' | 'reference_only' | 'failed' | 'cancelled';

type LooseRecord = Record<string, unknown>;

function record(value: unknown): LooseRecord {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as LooseRecord : {};
}

/** The Sorting Floor status an outcome is shown with. */
export function outcomeStatus(outcome: ImportOutcome): 'done' | 'error' {
    return outcome === 'failed' || outcome === 'cancelled' ? 'error' : 'done';
}

/** True for the outcomes that are a DONE row but not the plain success, so the row must say so. */
export function isNoticeOutcome(outcome: unknown): boolean {
    return outcome === 'partial' || outcome === 'reference_only';
}

/** What kind of file a row is, for wording only. */
export type ImportFileKind = 'document' | 'video' | 'other';

/** A short visible caption for the file card. Empty for the plain successes. */
export function outcomeCaption(outcome: unknown, kind: ImportFileKind = 'document'): string {
    switch (outcome) {
        case 'partial': return 'Partly read';
        case 'reference_only': return 'Reference only';
        case 'failed': return kind === 'video' ? 'Not transcribed' : 'Not read';
        case 'cancelled': return 'Cancelled';
        default: return '';
    }
}

/** The row's status message for an outcome (the long form; `detail` replaces it for a failure that has a specific reason). */
export function outcomeMessage(outcome: ImportOutcome, detail?: string | null, kind: ImportFileKind = 'document'): string {
    const reason = typeof detail === 'string' && detail.trim() ? detail.trim() : '';
    switch (outcome) {
        case 'partial': return 'Partly read (file kept)';
        case 'transcribed': return 'Transcribed';
        case 'reference_only': return reason || 'Kept as reference only (not transcribed)';
        case 'failed': return reason || (kind === 'video' ? 'Not transcribed (file kept)' : 'Could not read (file kept)');
        case 'cancelled': return 'Cancelled';
        default: return 'Processed';
    }
}

/**
 * A document record's outcome. extractionFailed wins; contentComplete false or contentTruncated true alone is PARTIAL, never failure.
 * A record with neither flag (a text file, an older project) is a plain success.
 */
export function documentImportOutcome(doc: unknown): ImportOutcome {
    const d = record(doc);
    if (d.extractionFailed === true) return 'failed';
    if (d.contentComplete === false || d.contentTruncated === true) return 'partial';
    return 'extracted';
}

/**
 * The project records a transcript for this clip, on its transcript row or its video row. Every writer that records one also
 * puts its words in the notes (video-transcript-notes.ts).
 */
export function videoHoldsTranscript(row: unknown, videoTranscripts?: unknown): boolean {
    return recordedVideoTranscript(row, videoTranscripts).length > 0;
}

/**
 * A saved video row's outcome, from the facts the import wrote on it and the project's transcript rows:
 *   a transcript                           transcribed, whatever an earlier attempt wrote on the row: its words are in the notes
 *   cancelled: true                        the operator stopped this clip (written only for the owning project, with nothing newer)
 *   error and not processed                failed
 *   processingMode reference_only, or
 *   skippedLargeProcessing / skipped       kept without transcription
 *   processed false                        never finished (saved mid-processing)
 *   anything else                          transcribed (including a row from before these facts existed)
 */
export function videoRowOutcome(row: unknown, videoTranscripts?: unknown): ImportOutcome {
    const v = record(row);
    if (videoHoldsTranscript(v, videoTranscripts)) return 'transcribed';
    if (v.cancelled === true && v.processed !== true) return 'cancelled';
    if (v.processed !== true && typeof v.error === 'string' && v.error.trim()) return 'failed';
    if (v.processed !== true && v.error && typeof v.error !== 'string') return 'failed';
    if (v.processingMode === 'reference_only' || v.skippedLargeProcessing === true || v.skipped === true) return 'reference_only';
    if (v.processed === false) return 'failed';
    return 'transcribed';
}

/** The detail a failed or cancelled video row carries, for its status message. */
export function videoRowDetail(row: unknown): string | null {
    const v = record(row);
    return typeof v.error === 'string' && v.error.trim() ? v.error.trim() : null;
}

/**
 * What one processed clip's RESULT (processStage1Video) says happened:
 *   success                       transcribed
 *   stopped with an error text    the operator cancelled this clip on its own project with nothing newer (the only stopped result
 *                                 that carries a message; the row keeps it as a persistent state)
 *   stopped without one           superseded or its project left the screen: it publishes NOTHING, so there is no outcome (null)
 *   anything else                 failed, with the classified message as the detail
 */
export function videoRunOutcome(result: unknown): { outcome: ImportOutcome | null; detail: string | null } {
    const r = record(result);
    const error = typeof r.error === 'string' && r.error.trim() ? r.error.trim() : null;
    if (r.success === true) return { outcome: 'transcribed', detail: null };
    if (r.stopped === true) return error ? { outcome: 'cancelled', detail: error } : { outcome: null, detail: null };
    return { outcome: 'failed', detail: error };
}

/**
 * What a settled Sorting Floor says, from its rows' status and outcome. The ONE summary read by the floor's header and footer and by
 * the Stage 1 action bar, so none of them can call a cancelled file "could not be read" or a reference-only clip "read".
 *   read           done and fully read (a photo, a fully extracted document, a transcribed clip)
 *   partial        done, partly read
 *   referenceOnly  done, kept without transcription
 *   failed         error that is not a cancellation (a file that could not be read, a clip that failed, any other error row)
 *   cancelled      error because the operator stopped the import
 */
export function summariseFloor(files: unknown): { total: number; read: number; partial: number; referenceOnly: number; failed: number; cancelled: number } {
    const list = Array.isArray(files) ? files : [];
    const summary = { total: list.length, read: 0, partial: 0, referenceOnly: 0, failed: 0, cancelled: 0 };
    for (const file of list) {
        const row = record(file);
        if (row.status === 'done') {
            if (row.outcome === 'partial') summary.partial += 1;
            else if (row.outcome === 'reference_only') summary.referenceOnly += 1;
            else summary.read += 1;
        } else if (row.status === 'error') {
            if (row.outcome === 'cancelled') summary.cancelled += 1;
            else summary.failed += 1;
        }
    }
    return summary;
}

/** Counts of each outcome among Sorting Floor files, for the footer wording. */
export function countOutcomes(files: unknown): { partial: number; referenceOnly: number } {
    const { partial, referenceOnly } = summariseFloor(files);
    return { partial, referenceOnly };
}
